/**
 * Fills the country selectors after page load.
 *
 * The header bar select, the mobile drawer select and the footer disclosure
 * list all used to be server-rendered with the complete
 * `localization.available_countries` list — four full copies in the HTML of
 * every page. That is a large slice of Liquid render time (TTFB) and well over
 * 100 KB of markup to parse before first paint, for a control almost nobody
 * touches.
 *
 * Now the page ships a stub with just the current country, and this script
 * fetches the real list once, on idle after `load`, from
 * `sections/localization-selector.liquid` via the Section Rendering API. Same
 * approach as assets/look-bundle-popup.js, just triggered by idle rather than
 * a click.
 *
 * The response is cached in sessionStorage so it is one request per session,
 * not per page view.
 */
(function () {
  'use strict';

  var SECTION = 'localization-selector';
  var SELECT_HOOK = '[data-loc-select]';
  var ROWS_HOOK = '[data-loc-rows]';

  var hydrated = false;
  var pending = false;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /**
   * The current country, read from the server-rendered stub rather than
   * `Shopify.country` — the stub is guaranteed to be there and to agree with
   * whatever Liquid resolved for this request.
   */
  function currentCountry() {
    var select = document.querySelector(SELECT_HOOK);
    if (select && select.value) return select.value;
    var hidden = document.querySelector('input[name="country_code"]');
    if (hidden && hidden.value) return hidden.value;
    return (window.Shopify && window.Shopify.country) || '';
  }

  /**
   * Cache per market + locale. The payload marks the current country `selected`
   * / `aria-current`, so a key that ignored the market would show the checkmark
   * against the wrong country after a switch.
   */
  function cacheKey() {
    var locale = (window.Shopify && window.Shopify.locale) || '';
    return 'lhg:loc:' + currentCountry() + ':' + locale + ':' + root();
  }

  function readCache() {
    try {
      return window.sessionStorage.getItem(cacheKey());
    } catch (e) {
      return null;
    }
  }

  function writeCache(html) {
    try {
      window.sessionStorage.setItem(cacheKey(), html);
    } catch (e) {
      /* private mode / quota — the fetch still worked, just don't cache. */
    }
  }

  /**
   * Section Rendering returns the section wrapped in
   * `<div id="shopify-section-localization-selector" class="shopify-section">`.
   * Pull out the two payload containers by id.
   */
  function parse(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var options = doc.getElementById('loc-options');
    var rows = doc.getElementById('loc-rows');
    if (!options || !rows) return null;
    return {
      options: options.innerHTML,
      rows: rows.innerHTML,
      showCurrencies: rows.getAttribute('data-show-currencies') === 'true',
    };
  }

  function fillSelects(optionsHtml) {
    var selects = document.querySelectorAll(SELECT_HOOK);
    for (var i = 0; i < selects.length; i++) {
      var select = selects[i];
      var current = select.value;
      select.innerHTML = optionsHtml;
      // The fetched markup already marks the current country `selected`. Only
      // re-assert it when that option actually exists — assigning an unmatched
      // value would blank the select.
      if (current && select.querySelector('option[value="' + current + '"]')) {
        select.value = current;
      }
    }
  }

  function fillRows(rowsHtml, showCurrencies) {
    var lists = document.querySelectorAll(ROWS_HOOK);
    for (var i = 0; i < lists.length; i++) {
      var list = lists[i];
      list.innerHTML = rowsHtml;

      var panel = list.closest('.country-selector__list');
      if (panel) {
        panel.classList.toggle('country-selector__list--with-multiple-currencies', showCurrencies);
      }

      // `LocalizationForm` (assets/js--all-dawn.js) binds its item clicks in the
      // constructor, so rows injected later are dead until we wire them up.
      // Everything else it uses — the button, panel, filter input — is
      // server-rendered, and filterCountries() re-queries the DOM each call.
      var host = list.closest('localization-form');
      if (host && typeof host.onItemClick === 'function') {
        var links = list.querySelectorAll('a');
        for (var j = 0; j < links.length; j++) {
          links[j].addEventListener('click', host.onItemClick.bind(host));
        }
      }
    }
  }

  function hydrate(html) {
    if (hydrated) return;
    var data = parse(html);
    if (!data) return;
    hydrated = true;
    document.removeEventListener('pointerdown', onEarlyInteraction, true);
    document.removeEventListener('focusin', onEarlyInteraction, true);
    fillSelects(data.options);
    fillRows(data.rows, data.showCurrencies);
  }

  function load() {
    if (hydrated || pending) return;
    if (!document.querySelector(SELECT_HOOK) && !document.querySelector(ROWS_HOOK)) return;

    var cached = readCache();
    if (cached) {
      hydrate(cached);
      return;
    }

    pending = true;
    var url = root() + '?section_id=' + SECTION;
    fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        writeCache(html);
        hydrate(html);
      })
      .catch(function () {
        /* Leave the stub in place — it still shows the current country. */
      })
      .finally(function () {
        pending = false;
      });
  }

  /**
   * Replaces the old inline `handleCountryChange`. Delegated so it works for
   * options injected after the fact. Germany is served in English by default,
   * so switching TO Germany also switches the locale.
   */
  document.addEventListener('change', function (event) {
    var select = event.target;
    if (!select || !select.matches || !select.matches(SELECT_HOOK)) return;

    var form = select.form;
    if (!form) return;

    var locale = form.querySelector('input[name="locale_code"]');
    if (locale && select.value === 'DE') locale.value = 'de';

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.submit();
    }
  });

  /**
   * Safety net for the narrow window between `load` and the idle callback: if a
   * shopper reaches for a selector first, fetch right away rather than let them
   * open an empty panel.
   */
  function onEarlyInteraction(event) {
    if (hydrated) return;
    var target = event.target;
    if (!target || !target.closest) return;
    if (target.closest(SELECT_HOOK) || target.closest('localization-form')) load();
  }

  document.addEventListener('pointerdown', onEarlyInteraction, true);
  document.addEventListener('focusin', onEarlyInteraction, true);

  function schedule() {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(load, { timeout: 3000 });
    } else {
      window.setTimeout(load, 200);
    }
  }

  if (document.readyState === 'complete') {
    schedule();
  } else {
    window.addEventListener('load', schedule, { once: true });
  }
})();
